import { useEffect, useRef } from "react";

type KeyboardShortcutOptions = {
  meta?: boolean;
  shift?: boolean;
  disabled?: boolean;
};

export default function useKeyboardShortcut(
  key: string,
  callback: () => void,
  options: KeyboardShortcutOptions = {}
) {
  const { meta, shift, disabled } = options;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    let suppressKeyUp = false;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (disabledRef.current) return;
      if (e.key !== key) return;
      if (meta && !(e.metaKey || e.ctrlKey)) return;
      if (shift && !e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      suppressKeyUp = true;
      callbackRef.current();
    };
    const keyUpHandler = (e: KeyboardEvent) => {
      if (suppressKeyUp && e.key === key) {
        e.preventDefault();
        e.stopImmediatePropagation();
        suppressKeyUp = false;
      }
    };
    document.addEventListener("keydown", handler, true);
    document.addEventListener("keyup", keyUpHandler, true);
    return () => {
      document.removeEventListener("keydown", handler, true);
      document.removeEventListener("keyup", keyUpHandler, true);
    };
  }, [key, meta, shift]);
}
